import React, { useState, useEffect, useRef } from 'react';
import '../style/inputfield.scss';

interface InputFieldProps {
  label: string;
  icon: React.ElementType;
  type: 'text' | 'tel' | 'mail';
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  required?: boolean;
  placeholder?: string;
  generalContactError?: string | null;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  icon: Icon,
  type,
  value,
  onChange,
  error,
  required = false,
  placeholder,
  generalContactError,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const lastPhoneValue = useRef<string>('');
  const initialPhoneSet = useRef<boolean>(false);

  useEffect(() => {
    if (type === 'tel' && isFocused && inputRef.current) {
      setTimeout(() => {
        const val = inputRef.current?.value || '';
        let newPos;

        if (val === '+7') {
          newPos = 2;
        } else {
          newPos = val.length;
        }

        inputRef.current.setSelectionRange(newPos, newPos);
      }, 0);
    }
  }, [isFocused, type, value]);

  const formatPhoneNumber = (phoneNumberString: string): string => {
    const defaultPrefix = '+7';
    let rawValue = phoneNumberString.replace(/[^\d]/g, '');

    if (rawValue.startsWith('8')) {
      rawValue = '7' + rawValue.substring(1);
    } else if (!rawValue.startsWith('7') && rawValue.length > 0) {
      rawValue = '7' + rawValue;
    }

    if (!rawValue || rawValue === '7') {
      return isFocused ? defaultPrefix : '';
    }

    let formattedNumber = defaultPrefix;
    const digits = rawValue.substring(1);

    if (digits.length > 0) {
      formattedNumber += ' (';
      if (digits.length > 0) {
        formattedNumber += digits.substring(0, 3);
      }
      if (digits.length >= 4) {
        formattedNumber += ') ';
        formattedNumber += digits.substring(3, 6);
      }
      if (digits.length >= 7) {
        formattedNumber += '-';
        formattedNumber += digits.substring(6, 8);
      }
      if (digits.length >= 9) {
        formattedNumber += '-';
        formattedNumber += digits.substring(8, 10);
      }
    }
    return formattedNumber.substring(0, 18);
  };

  const sanitizeInput = (input: string): string => {
    const xssPatterns = /<script.*?>|<\/script>|<img.*?onerror=|on[a-zA-Z]+=|javascript:|data:text\/html|expression\(/ig;
    let sanitized = input.replace(xssPatterns, '');
    sanitized = sanitized.trim();
    return sanitized;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    let processedValue = inputValue;

    if (type === 'tel') {
      const oldPhone = lastPhoneValue.current;
      const oldSelectionStart = event.target.selectionStart || 0;

      let newRawDigits = inputValue.replace(/[^\d]/g, '');
      if (newRawDigits.length > 0 && !newRawDigits.startsWith('7')) {
        if (newRawDigits.startsWith('8')) {
          newRawDigits = '7' + newRawDigits.substring(1);
        } else {
          newRawDigits = '7' + newRawDigits;
        }
      } else if (newRawDigits.length === 0) {
        newRawDigits = '';
      }

      let rawDigitsBeforeOldCursor = 0;
      for (let i = 0; i < oldSelectionStart && i < oldPhone.length; i++) {
        if (/\d/.test(oldPhone[i])) {
          rawDigitsBeforeOldCursor++;
        }
      }

      const oldRawDigits = oldPhone.replace(/[^\d]/g, '');
      const rawDigitsChange = newRawDigits.length - oldRawDigits.length;

      if (rawDigitsChange < 0 || oldSelectionStart < 2) {
        rawDigitsBeforeOldCursor = 0;
        for (let i = 0; i < oldSelectionStart && i < inputValue.length; i++) {
          if (/\d/.test(inputValue[i])) {
            rawDigitsBeforeOldCursor++;
          }
        }
      } else if (rawDigitsChange > 0) {
        if (/\d/.test(inputValue[oldSelectionStart - 1])) {
          rawDigitsBeforeOldCursor++;
        }
      }

      const newFormattedPhone = formatPhoneNumber(newRawDigits);

      let newCursorPos = 0;
      let digitsCounted = 0;
      for (let i = 0; i < newFormattedPhone.length; i++) {
        if (/\d/.test(newFormattedPhone[i])) {
          digitsCounted++;
        }
        if (digitsCounted === rawDigitsBeforeOldCursor) {
          newCursorPos = i + 1;
          break;
        }
      }

      if (newCursorPos === 0 && newFormattedPhone.length > 0) {
        let lastValidPos = 0;
        for (let i = 0; i < newFormattedPhone.length; i++) {
          if (/\d/.test(newFormattedPhone[i]) || newFormattedPhone[i] === '(' || newFormattedPhone[i] === ')' || newFormattedPhone[i] === '-' || newFormattedPhone[i] === ' ') {
            lastValidPos = i + 1;
          }
        }
        newCursorPos = lastValidPos;
      }

      if (newCursorPos < 2 && newFormattedPhone.length > 0) {
        newCursorPos = 2;
      }

      onChange(newFormattedPhone);
      lastPhoneValue.current = newFormattedPhone;

      setTimeout(() => {
        if (inputRef.current) {
          let finalPos = newCursorPos;
          const val = inputRef.current.value;

          while (finalPos < val.length && !/\d/.test(val[finalPos]) && val[finalPos] !== '+') {
            finalPos++;
          }

          if (rawDigitsChange < 0) {
            while (finalPos > 2 && !/\d/.test(val[finalPos - 1]) && val[finalPos - 1] !== '+') {
              finalPos--;
            }
          }

          if (finalPos < 2 && newFormattedPhone.length > 0) {
            finalPos = 2;
          }

          inputRef.current.setSelectionRange(finalPos, finalPos);
        }
      }, 0);
    } else if (type === 'mail') {
      processedValue = inputValue.replace(/[а-яА-ЯёЁ]/g, '');
      processedValue = sanitizeInput(processedValue);
      onChange(processedValue);
    } else {
      processedValue = sanitizeInput(inputValue);
      onChange(processedValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'tel') {
      const inputElement = event.currentTarget;
      const start = inputElement.selectionStart || 0;
      const end = inputElement.selectionEnd || 0;
      const val = inputElement.value;

      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (start === 0 && end === val.length && val.length > 0) {
          event.preventDefault();
          onChange('');
          lastPhoneValue.current = '';
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(0, 0);
            }
          }, 0);
          return;
        }

        if (start <= 2 && end >= 2) {
          event.preventDefault();
          inputElement.setSelectionRange(2, 2);
        } else if (event.key === 'Backspace' && start === 2) {
          event.preventDefault();
        } else if (event.key === 'Backspace' && start > 2) {
          if (start === end) {
            let newStart = start;
            while (newStart > 2 && !/\d/.test(val[newStart - 1])) {
              newStart--;
            }
            if (newStart !== start) {
              event.preventDefault();
              inputElement.setSelectionRange(newStart, newStart);
            }
          }
        } else if (event.key === 'Delete' && start < val.length) {
          if (start === end) {
            let newEnd = start;
            while (newEnd < val.length && !/\d/.test(val[newEnd])) {
              newEnd++;
            }
            if (newEnd !== start) {
              event.preventDefault();
              inputElement.setSelectionRange(newEnd, newEnd);
            }
          }
        }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        setTimeout(() => {
          const currentPos = inputElement.selectionStart || 0;
          let newPos = currentPos;
          if (event.key === 'ArrowLeft') {
            while (newPos > 2 && !/\d/.test(val[newPos - 1])) {
              newPos--;
            }
            if (newPos < 2 && val.length > 0) {
              newPos = 2;
            }
          } else {
            while (newPos < val.length && !/\d/.test(val[newPos])) {
              newPos++;
            }
          }
          if (newPos !== currentPos) {
            inputElement.setSelectionRange(newPos, newPos);
          }
        }, 0);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (type === 'tel' && value === '' && !initialPhoneSet.current) {
      onChange('+7');
      lastPhoneValue.current = '+7';
      initialPhoneSet.current = true;
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (type === 'tel') {
      const rawPhone = value.replace(/[^\d]/g, '');
      if (rawPhone.length === 1 && rawPhone === '7') {
        onChange('');
        initialPhoneSet.current = false;
      }
    }
  };

  const getDisplayPlaceholder = () => {
    if (error) return error;
    if (generalContactError && (type === 'tel' || type === 'mail')) return generalContactError;
    return placeholder || '';
  };

  const inputClassName = `styled-input body-m ${
    type === 'tel' && !isFocused && value === '' ? 'is-initial-phone' : ''
  }`;

  const defaultInputClass = `default-input ${error ? 'error' : (generalContactError && (type === 'tel' || type === 'mail')) ? 'error' : ''} ${error && type === 'text' && label === 'Контакт' ? 'warning' : ''}`;
  const inputFieldWrapperClass = `input-field-wrapper ${error ? 'border-error' : (generalContactError && (type === 'tel' || type === 'mail')) ? 'border-error' : ''} ${error && type === 'text' && label === 'Контакт' ? 'border-warning' : ''}`;
  const iconClass = `input-icon ${error ? 'icon-error' : (generalContactError && (type === 'tel' || type === 'mail')) ? 'icon-error' : ''} ${error && type === 'text' && label === 'Контакт' ? 'icon-warning' : ''}`;

  const getInputTypeAttribute = () => {
    if (type === 'mail') {
      return 'email';
    }
    return type === 'tel' ? 'tel' : 'text';
  };

  return (
    <div className={defaultInputClass}>
      <label className="label">
        <div className="container">
          <div className="text-container body-m">{label}</div>
          {required && <div className="required">*</div>}
        </div>
      </label>
      <div className={inputFieldWrapperClass}>
        <Icon className={iconClass} />
        <input
          type={getInputTypeAttribute()}
          placeholder={getDisplayPlaceholder()}
          className={inputClassName}
          value={type === 'tel' ? formatPhoneNumber(value) : value}
          onChange={handleChange}
          onKeyDown={type === 'tel' ? handleKeyDown : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={inputRef}
          inputMode={type === 'mail' ? 'email' : undefined}
        />
      </div>
    </div>
  );
};

export default InputField;